import { enumMembers, type ApiSchema } from "./apiSchema";
import { requestJson } from "./http/httpClient";
import { readNullableString, readObject, readOneOf } from "./http/jsonDecoding";

/**
 * The guided connector setup's stored state (onboarding.md OB-2; route
 * `server/src/dmw/api/routes/accounts/connector_setup.py`): whether the signed-in person met the
 * guide and whether they put it away. How far the setup got is derived from the connectors.
 */

export type ConnectorSetupGuideState = ApiSchema<"ConnectorSetupGuideState">;
export type ConnectorSetupGuideEvent = "introduce" | "dismiss" | "resume";

export interface ConnectorSetupGuide {
  readonly state: ConnectorSetupGuideState;
  /** ISO 8601: the last event; `null` before the first. */
  readonly changedAt: string | null;
}

const GUIDE_STATES = enumMembers<ConnectorSetupGuideState>({ not_introduced: true, active: true, dismissed: true });

const PATH = "/accounts/connector-setup";

export function readConnectorSetupGuide(signal?: AbortSignal): Promise<ConnectorSetupGuide> {
  return requestJson({ method: "GET", path: PATH, signal, decodeResponse: decodeGuide });
}

/** Apply one event; fails with `illegal_state_transition` where the guide's machine has none. */
export function applyConnectorSetupGuideEvent(event: ConnectorSetupGuideEvent): Promise<ConnectorSetupGuide> {
  return requestJson({ method: "POST", path: `${PATH}/${event}`, decodeResponse: decodeGuide });
}

function decodeGuide(body: unknown): ConnectorSetupGuide {
  const guide = readObject(body, "connector setup guide");
  return { state: readOneOf(guide, "state", GUIDE_STATES), changedAt: readNullableString(guide, "changed_at") };
}
